/**
 * JudgeReportCard — displays Content Judge results (30 rule checks + LLM critique)
 * on the BlogDetailPage. Shows overall score, sub-scores, rule check summary,
 * strengths, and findings with an "Apply Fixes" action.
 *
 * Uses the existing useJudgeReport / useRefreshJudgeReport / useApplyJudgeFixes hooks.
 */

import React, { useState } from 'react';
import { Card, Button, Tag, Collapse, Tooltip } from 'antd';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Wand2,
  ChevronDown,
  ChevronRight,
  Shield,
  Star,
} from 'lucide-react';
import { FixingOverlay } from '@/components/ui/FixingLoader';
import { useJudgeReport, useRefreshJudgeReport, useApplyJudgeFixes } from '@/hooks/useBlogCreationAPI';
import type { JudgeRuleCheck, JudgeFinding, JudgeScores } from '@/services/blogCreationService';

interface JudgeReportCardProps {
  contentId: string;
}

const CHECK_ICON: Record<string, React.ReactNode> = {
  pass: <CheckCircle size={12} className="text-teal-dark shrink-0" />,
  warn: <AlertTriangle size={12} className="text-yellow-600 dark:text-yellow-400 shrink-0" />,
  fail: <XCircle size={12} className="text-destructive shrink-0" />,
};

function scoreColor(n: number | undefined | null): string {
  if (!n) return 'text-muted-foreground';
  if (n >= 8) return 'text-teal-dark';
  if (n >= 6) return 'text-yellow-600 dark:text-yellow-400';
  if (n >= 4) return 'text-coral';
  return 'text-destructive';
}

function overallBadge(overall: number | null, fails: number, warns: number) {
  if (overall === null) return <Tag color="default">No Score</Tag>;
  if (overall >= 8 && fails === 0) return <Tag color="success">Good</Tag>;
  if (overall >= 6) return <Tag color="warning">Review Recommended</Tag>;
  return <Tag color="error">Needs Work</Tag>;
}

const JudgeReportCard: React.FC<JudgeReportCardProps> = ({ contentId }) => {
  const { data: report, isLoading } = useJudgeReport(contentId, { enabled: true });
  const refreshMutation = useRefreshJudgeReport();
  const applyFixesMutation = useApplyJudgeFixes();
  const [checksExpanded, setChecksExpanded] = useState(false);

  // Don't render anything while loading or if no report
  if (isLoading) {
    return (
      <Card
        size="small"
        className="border border-border shadow-sm animate-pulse"
        bodyStyle={{ padding: '12px 16px' }}
      >
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading judge report...</span>
        </div>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card
        size="small"
        className="border border-border shadow-sm"
        bodyStyle={{ padding: '12px 16px' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">No judge report available</span>
          </div>
          <Button
            size="small"
            icon={<RefreshCw size={12} />}
            onClick={() => refreshMutation.mutate(contentId)}
            loading={refreshMutation.isPending}
          >
            Run Judge
          </Button>
        </div>
      </Card>
    );
  }

  const passes = report.rule_checks.filter((c: JudgeRuleCheck) => c.status === 'pass').length;
  const warns = report.rule_checks.filter((c: JudgeRuleCheck) => c.status === 'warn').length;
  const fails = report.rule_checks.filter((c: JudgeRuleCheck) => c.status === 'fail').length;
  const scores: JudgeScores = report.scores || {};
  const hasFindings = report.findings && report.findings.length > 0;

  return (
    <Card
      size="small"
      className="relative border border-border shadow-sm"
      bodyStyle={{ padding: 0 }}
    >
      <FixingOverlay show={applyFixesMutation.isPending} label="Applying fixes…" />
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield size={16} className="text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">Content Judge</span>
          <span className={`text-lg font-bold ${scoreColor(report.overall)}`}>
            {report.overall ?? '—'}<span className="text-xs font-normal text-muted-foreground">/10</span>
          </span>
          {overallBadge(report.overall, fails, warns)}
        </div>
        <div className="flex items-center gap-2">
          {hasFindings && (
            <Tooltip title="Apply automated fixes to content">
              <Button
                size="small"
                type="primary"
                icon={<Wand2 size={12} />}
                onClick={() => applyFixesMutation.mutate(contentId)}
                loading={applyFixesMutation.isPending}
              >
                Apply Fixes
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Re-run judge analysis">
            <Button
              size="small"
              icon={<RefreshCw size={12} />}
              onClick={() => refreshMutation.mutate(contentId)}
              loading={refreshMutation.isPending}
            />
          </Tooltip>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Sub-scores grid */}
        {(scores.writing_quality || scores.seo_coherence || scores.content_value || scores.reader_experience) && (
          <div className="grid grid-cols-4 gap-2">
            {([
              ['Writing', scores.writing_quality],
              ['SEO', scores.seo_coherence],
              ['Value', scores.content_value],
              ['UX', scores.reader_experience],
            ] as [string, number | undefined][]).map(([label, score]) => (
              <div key={label} className="text-center rounded-md border border-border bg-muted py-1.5 px-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className={`text-sm font-bold ${scoreColor(score)}`}>{score ?? '—'}/10</div>
              </div>
            ))}
          </div>
        )}

        {/* Rule checks summary */}
        <div>
          <button
            className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground w-full text-left"
            onClick={() => setChecksExpanded(!checksExpanded)}
          >
            {checksExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Rule Checks ({report.rule_checks.length})
            <span className="ml-auto flex items-center gap-2 text-[10px] font-normal">
              <span className="text-teal-dark">{passes} pass</span>
              {warns > 0 && <span className="text-yellow-600 dark:text-yellow-400">{warns} warn</span>}
              {fails > 0 && <span className="text-destructive">{fails} fail</span>}
            </span>
          </button>

          {checksExpanded && (
            <div className="mt-2 space-y-0.5 max-h-52 overflow-y-auto border border-border rounded-md p-2 bg-muted/50">
              {report.rule_checks.map((check: JudgeRuleCheck) => (
                <div key={check.name} className="flex items-start gap-2 text-xs py-0.5">
                  {CHECK_ICON[check.status] || CHECK_ICON.pass}
                  <span className="font-medium text-foreground w-28 shrink-0">{check.name}</span>
                  <span className="text-muted-foreground truncate">{check.detail}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Strengths */}
        {report.strengths && report.strengths.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1.5">
              <Star size={12} className="text-teal" />
              Strengths
            </div>
            <ul className="space-y-0.5">
              {report.strengths.map((s: string, i: number) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className="text-teal-dark mt-0.5 shrink-0">+</span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Findings */}
        {hasFindings && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1.5">
              <AlertTriangle size={12} className="text-coral" />
              Issues ({report.findings.length})
            </div>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {report.findings.map((f: JudgeFinding, i: number) => (
                <div key={i} className="rounded border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs space-y-0.5">
                  <div className="flex items-start gap-1.5">
                    <XCircle size={11} className="text-destructive shrink-0 mt-0.5" />
                    <span className="text-foreground">{f.issue}</span>
                  </div>
                  {f.fix && (
                    <div className="pl-4 text-muted-foreground italic">Fix: {f.fix}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Verdict */}
        {report.verdict && (
          <div className="text-xs text-muted-foreground italic border-t border-border pt-2">
            {report.verdict}
          </div>
        )}
      </div>
    </Card>
  );
};

export default JudgeReportCard;
